/* eslint-disable */
import saveStoreDraft from "../../../utils/saveStoreDraft";
import useStoreFormDraft from "../../../hooks/useStoreFormDraft";
import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { applyStoreDraft } from "../../../redux/store/store";
import { doc } from "firebase/firestore";
import { db } from "../../../firebase";
import Swal from "sweetalert2";
import { RefreshCw, Info, X, Activity } from "lucide-react";
import apicache from "../../../components/clearCach/clear";
import { useTranslation } from "react-i18next";

export default function ActivationMicrosoftClarity({ close }) {
  const account = useSelector((state) => state.AccountReducer);
  const store = useSelector((state) => state.StoreReducer);
  const products = useSelector((state) => state.ProductsReducer);
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(false);
  const [data, setData] = useStoreFormDraft(store);
  const [isActive, setIsActive] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {

    setIsActive(Boolean(store?.microsoftClarity));
  }, [store]);

  const showAlert = (icon, title, text) => {
    return Swal.fire({
      icon,
      title,
      text,
      confirmButtonText: t("servicePageActivation.microsoftClarity.alerts.ok"),
      confirmButtonColor: "rgb(141, 93, 167)",
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: true,
      backdrop: `
        rgba(0,0,0,0.7)
      `,
      position: "center",
    });
  };

  const clearValue = () => {
    setData({ ...data, microsoftClarity: "" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const clarityId = (data.microsoftClarity || "").trim();
    const isDeactivating = clarityId === "";

    if (isDeactivating) {
      const result = await Swal.fire({
        icon: "warning",
        title: t("servicePageActivation.microsoftClarity.alerts.deactivateTitle"),
        text: t("servicePageActivation.microsoftClarity.alerts.deactivateText"),
        confirmButtonText: t("servicePageActivation.microsoftClarity.alerts.ok"),
        confirmButtonColor: "rgb(141, 93, 167)",
        showCancelButton: true,
        cancelButtonText: t("services.cancel"),
      });

      if (!result.isConfirmed) {
        return;
      }
    }

    apicache(account, products);
    setLoading(true);

    try {
      const updatedData = {
        ...data,
        microsoftClarity: clarityId,
      };
      await saveStoreDraft(doc(db, "store", data.id), store, updatedData);
      dispatch(applyStoreDraft(store, updatedData));

      setLoading(false);
      setIsActive(!isDeactivating);

      if (isDeactivating) {
        await showAlert(
          "success",
          t("servicePageActivation.microsoftClarity.alerts.deactivatedSuccess"),
          "",
        );
      } else {
        await showAlert(
          "success",
          t("servicePageActivation.microsoftClarity.alerts.activatedSuccess"),
          "",
        );
      }

      close();
    } catch (error) {
      setLoading(false);

      if (isDeactivating) {
        await showAlert(
          "error",
          t("servicePageActivation.microsoftClarity.alerts.deactivationErrorTitle"),
          t("servicePageActivation.microsoftClarity.alerts.errorText"),
        );
      } else {
        await showAlert(
          "error",
          t("servicePageActivation.microsoftClarity.alerts.activationErrorTitle"),
          t("servicePageActivation.microsoftClarity.alerts.errorText"),
        );
      }
    }
  };

  return (
    <div className="min-h-[400px] h-fit p-6">
      <div className="mb-6 dark:!bg-[#181919] dark:!border-[#3c3a3d] border p-6 rounded-xl">
        <h1 className="text-xl font-bold text-[#9e68ba] mb-2 flex items-center">
          <Activity className="ml-2" size={18} />
          {t("servicePageActivation.microsoftClarity.title")}
        </h1>
        <p className="text-gray-600 dark:!text-gray-300">
          {t("servicePageActivation.microsoftClarity.description")}
        </p>
      </div>

      <div
        className={`mb-4 p-3 rounded-lg ${
          isActive
            ? "bg-green-50 border border-green-200 dark:!bg-[#181919] dark:!border-[#3c3a3d]"
            : "bg-gray-50 border border-gray-200 dark:!bg-[#181919] dark:!border-[#3c3a3d]"
        }`}
      >
        <div className="flex gap-x-2 items-center">
          <div
            className={`w-3 h-3 rounded-full mr-2 ${
              isActive ? "bg-green-500" : "bg-gray-400 dark:!bg-gray-500"
            }`}
          ></div>
          <span
            className={`text-sm font-medium ${
              isActive ? "text-green-700" : "text-gray-700 dark:!text-gray-300"
            }`}
          >
            {isActive
              ? t("servicePageActivation.microsoftClarity.statusActive")
              : t("servicePageActivation.microsoftClarity.statusInactive")}
          </span>
        </div>
      </div>

      <div className="bg-white dark:!bg-[#181919] rounded-xl shadow-sm border border-[#fbf4ff] dark:!border-[#3c3a3d] p-6 mb-6">
        <form onSubmit={handleSubmit}>
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-[#9e68ba] mb-4 flex items-center">
              <Activity className="ml-2" size={18} />
              {t("servicePageActivation.microsoftClarity.settingsTitle")}
            </h3>

            <div className="mb-5">
              <div className="flex justify-between items-center mb-2">
                <label className="block text-sm font-medium text-gray-700 dark:!text-gray-300">
                  {t("servicePageActivation.microsoftClarity.clarityIdLabel")}
                </label>
                <div
                  className={`px-2 py-1 rounded-full text-xs font-medium ${
                    isActive
                      ? "bg-green-100 text-green-800 dark:!bg-green-900 dark:!text-green-300"
                      : "bg-gray-100 text-gray-800 dark:!bg-gray-700 dark:!text-gray-300"
                  }`}
                >
                  {isActive
                    ? t("servicePageActivation.microsoftClarity.statusEnabled")
                    : t("servicePageActivation.microsoftClarity.statusDisabled")}
                </div>
              </div>

              <div className="flex flex-col md:flex-row gap-3">
                <div className="relative flex-grow">
                  <input
                    type="text"
                    placeholder={t("ui.projectExample")}
                    value={data.microsoftClarity || ""}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\s+/g, "");
                      setData({ ...data, microsoftClarity: value });
                    }}
                    className="w-full px-3 py-2 border border-gray-300 dark:!border-[#3c3a3d] rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-200 focus:border-purple-500 dark:!bg-[#141414] dark:!text-gray-200 pr-10"
                  />
                  {data.microsoftClarity && (
                    <button
                      type="button"
                      onClick={clearValue}
                      className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-red-500 transition-colors w-6 h-6 flex items-center justify-center rounded-full hover:bg-gray-100 dark:hover:!bg-gray-600"
                      title={t("servicePageActivation.microsoftClarity.clearTitle")}
                    >
                      <X size={16} />
                    </button>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className={`px-6 py-2.5 ${
                    data.microsoftClarity
                      ? "bg-[#9b59bf] hover:bg-[#814a9f]"
                      : "bg-red-500 hover:bg-red-600"
                  } text-white font-medium rounded-lg transition-colors flex items-center justify-center min-w-[120px] ${
                    loading ? "opacity-70 cursor-not-allowed" : ""
                  }`}
                >
                  {loading ? (
                    <RefreshCw className="animate-spin" size={18} />
                  ) : data.microsoftClarity ? (
                    t("servicePageActivation.microsoftClarity.activate")
                  ) : (
                    t("servicePageActivation.microsoftClarity.deactivate")
                  )}
                </button>
              </div>

              {!data.microsoftClarity && !isActive && (
                <p className="text-amber-600 text-sm mt-1">
                  {t("servicePageActivation.microsoftClarity.clarityIdInactiveWarning")}
                </p>
              )}
            </div>

            <div className="mt-2 text-sm text-gray-600 dark:!text-gray-400">
              <p>{t("servicePageActivation.microsoftClarity.helpText1")}</p>
              <p>{t("servicePageActivation.microsoftClarity.helpText2")}</p>
            </div>
          </div>
        </form>
      </div>

      <div className="bg-blue-50 dark:!bg-[#181919] rounded-xl p-5 border border-blue-100 dark:!border-[#3c3a3d] mb-6">
        <h3 className="text-lg font-semibold text-blue-800 dark:!text-gray-200 mb-3 flex items-center">
          <Info className="ml-2" size={18} />
          {t("servicePageActivation.microsoftClarity.howToFind.title")}
        </h3>
        <ol className="list-decimal list-inside space-y-2 text-blue-700 dark:!text-gray-400 mr-2">
          <li>{t("servicePageActivation.microsoftClarity.howToFind.step1")}</li>
          <li>{t("servicePageActivation.microsoftClarity.howToFind.step2")}</li>
          <li>{t("servicePageActivation.microsoftClarity.howToFind.step3")}</li>
          <li>{t("servicePageActivation.microsoftClarity.howToFind.step4")}</li>
          <li>{t("servicePageActivation.microsoftClarity.howToFind.step5")}</li>
        </ol>
      </div>
    </div>
  );
}
